<template>
  <div class="gift-code-index-page">
    <div class="feedback">
      <transition name="fade" @enter="enterSuccessFeedback">
        <FeedbackSuccess
          v-if="showableSuccessFeedback"
          :message="successMessage"
        />
      </transition>
      <transition name="fade" @enter="enterErrorFeedback">
        <FeedbackError v-if="showableErrorFeedback" :message="errorMessage" />
      </transition>
    </div>
    <section class="gift-code-section">
      <div v-if="!isApp" class="gift-code-section__head">
        <PageHeadline title="ギフトコード" />
      </div>
      <div class="gift-code-section__body">
        <div class="gift-code-list__content-wrapper">
          <div class="gift-code-form__content">
            <div v-if="errorMessages.length > 0" class="box-list--alert">
              <div
                v-for="(errorMessageText, index) in errorMessages"
                :key="index"
                class="error_message"
              >
                <p>{{ errorMessageText }}</p>
              </div>
            </div>
            <p class="body-bold gift-code-form__text">
              お手元にギフトコードをお持ちの方
            </p>
            <span class="gift-code-form__caption caption">
              半角英数字14文字以内
            </span>
            <form
              :action="allocationPath"
              method="post"
              class="gift-code-form"
              accept-charset="UTF-8"
            >
              <input
                type="hidden"
                name="authenticity_token"
                :value="authenticityToken"
              />
              <input
                v-model.trim="giftCode"
                type="text"
                name="gift_code_mypage_serial_form[gift_code]"
                :class="{
                  'gift-code-field': !isInputted,
                  'gift-code-field--inputted': isInputted
                }"
                placeholder="ギフトコードを入力してください"
                maxlength="14"
              />
              <input
                v-if="isInputted"
                type="submit"
                value="登録する"
                class="gift-code-submit"
              />
            </form>
          </div>
        </div>
        <div class="gift-code-list__content-wrapper">
          <div
            v-if="availableSerials.length > 0"
            class="gift-code-list__content"
          >
            <div class="gift-code-list__content-title-wrap subhead-bold">
              <span class="gift-code-list__content-title">利用可能</span>
            </div>
            <a
              v-for="serial in formattedAvailableSerials"
              :key="serial.id"
              :href="`/mypage/gift_codes/${serial.id}`"
              class="gift-code-link chevron--forward--black"
            >
              <span class="gift-code-link__text--bold">{{
                serial.serial
              }}</span>
              <span
                class="gift-code-link__text gift-code-link__expiration-date"
              >
                {{ serial.availablePeriodEndAtText }}
              </span>
            </a>
          </div>
          <div v-else class="gift-code-list__content--empty">
            <p class="hint">利用できるギフトコードがありません</p>
          </div>
        </div>
        <div
          v-if="unavailableSerials.length > 0"
          class="gift-code-list__content-wrapper"
        >
          <div class="gift-code-list__content">
            <div
              class="gift-code-list__content-title-wrap subhead-bold tl-list__text"
              :class="
                showableAccordion
                  ? 'expand-tl-list--active'
                  : 'expand-tl-list--no-border'
              "
              @click="showAccordion"
            >
              <span
                class="gift-code-list__content-title"
                :class="
                  showableAccordion
                    ? 'chevron--up--black'
                    : 'chevron--down--black'
                "
              >
                利用済み・期限切れ
              </span>
            </div>
            <transition
              name="accordion"
              @before-enter="beforeEnter"
              @enter="enter"
              @before-leave="beforeLeave"
              @leave="leave"
            >
              <div v-show="showableAccordion" class="expand-tl-list__ul">
                <a
                  v-for="serial in formattedUnavailableSerials"
                  :key="serial.id"
                  :href="`/mypage/gift_codes/${serial.id}`"
                  class="gift-code-link chevron--forward--black"
                >
                  <span class="gift-code-link__text--bold">{{
                    serial.serial
                  }}</span>
                  <span
                    class="gift-code-link__text gift-code-link__expiration-date"
                  >
                    {{ serial.availablePeriodEndAtText }}
                  </span>
                </a>
              </div>
            </transition>
          </div>
        </div>
        <div v-if="!isApp" class="gift-code-foot__content-wrapper">
          <div class="gift-code-foot__giftee-top-link-area">
            <a :href="rootPath" class="text-link-to-top">
              gifteeトップへ戻る
            </a>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import dayjs from 'dayjs'
import FeedbackSuccess from '../../../atoms/feedbacks/feedback-success.vue'
import FeedbackError from '../../../atoms/feedbacks/feedback-error.vue'
import PageHeadline from '../../../molecules/headlines/PageHeadline.vue'

const props = defineProps({
  isApp: {
    type: Boolean,
    required: true
  },
  availableSerials: {
    type: Array,
    required: true
  },
  unavailableSerials: {
    type: Array,
    required: true
  },
  allocationPath: {
    type: String,
    required: true
  },
  authenticityToken: {
    type: String,
    required: true
  },
  errorMessages: {
    type: Array,
    required: true
  },
  flashNotice: {
    type: String,
    required: false,
    default: ''
  },
  rootPath: {
    type: String,
    required: true
  },
  initialGiftCode: {
    type: String,
    required: false,
    default: ''
  }
})

const giftCode = ref(props.initialGiftCode)
const showableSuccessFeedback = ref(false)
const successMessage = ref('')
const showableErrorFeedback = ref(false)
const errorMessage = ref('')
const showableAccordion = ref(false)

const isInputted = computed(() => giftCode.value.length !== 0)

const formatAvailablePeriodEndAt = date => {
  return `${dayjs(date).format('YYYY年M月D日')}まで有効`
}

const formattedAvailableSerials = computed(() =>
  props.availableSerials.map(serial => ({
    ...serial,
    availablePeriodEndAtText: formatAvailablePeriodEndAt(
      serial.available_period_end_at
    )
  }))
)

const formattedUnavailableSerials = computed(() =>
  props.unavailableSerials.map(serial => ({
    ...serial,
    availablePeriodEndAtText: formatAvailablePeriodEndAt(
      serial.available_period_end_at
    )
  }))
)

onMounted(() => {
  if (props.flashNotice) {
    successMessage.value = props.flashNotice
    showableSuccessFeedback.value = true
  }
  if (props.errorMessages.length > 0) {
    errorMessage.value = 'ギフトコードにエラーがあります'
    showableErrorFeedback.value = true
  }
})

const enterSuccessFeedback = () => {
  setTimeout(() => {
    showableSuccessFeedback.value = false
  }, 3000)
}

const enterErrorFeedback = () => {
  setTimeout(() => {
    showableErrorFeedback.value = false
  }, 3000)
}

const showAccordion = () => {
  showableAccordion.value = !showableAccordion.value
}

const beforeEnter = el => {
  el.style.height = '0'
}

const enter = el => {
  el.style.height = el.scrollHeight + 'px'
}

const beforeLeave = el => {
  el.style.height = el.scrollHeight + 'px'
}

const leave = el => {
  el.style.height = '0'
}
</script>

<style lang="scss" scoped>
.gift-code-index-page {
  background-color: $gray-3;
  text-align: left;
}

.gift-code-section__head {
  margin-bottom: 16px;
}

.gift-code-list {
  &__content-wrapper {
    background-color: $white;
    margin-bottom: 16px;

    &:last-of-type {
      margin-bottom: 0;
    }
  }

  &__content {
    margin: auto;
    max-width: $small-contents-width;
    text-align: left;
  }

  &__content-title-wrap {
    padding: 16px;
    border-bottom: 1px $gray-2 solid;

    &:last-child {
      border-bottom: 0;
    }
  }

  &__content-title {
    padding-right: 24px;
  }

  &__content--empty {
    margin: auto;
    padding: 40px 16px;
    max-width: $small-contents-width;
    box-sizing: border-box;
  }
}

.gift-code-foot {
  &__content-wrapper {
    margin: 16px auto 0;
    background: $gray-3;
  }

  &__giftee-top-link-area {
    padding: 16px;
    display: flex;
    justify-content: center;
    justify-items: center;
    background: $gray-3;

    .text-link-to-top {
      display: inline;
      color: $link;
    }
  }
}

.gift-code-form {
  &__content {
    margin: auto;
    padding: 24px 16px;
    max-width: $small-contents-width;
    box-sizing: border-box;
    text-align: left;

    .box-list--alert {
      margin-bottom: 16px;
    }
  }

  &__text {
    margin: 0 0 16px;
  }

  &__caption {
    margin-bottom: 4px;
    display: inline-block;
  }
}
</style>
